import { type TreeTopic, suggestSims, useKnowledgeTree } from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { Spinner } from '../components/ui';
import {
  Block,
  DARK,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { ArrowCounterClockwiseIcon, ArrowUpRightIcon, CheckIcon } from '../design/icons';

/*
 * «Дерево знань» in «Форми 2» (28.10). The title on the paper with the count of what is learnt; the
 * subjects on the left as a list with their pictures and how far each has grown (on a phone a row of
 * chips); on the right the chosen subject's topics growing from the bottom up along a thin trunk: learnt
 * — a mint mark with a tick, to repeat — coral with the way round and the pills to the simulation and the
 * materials, now — a soft card in the subject's colour with its picture big and quiet behind, ahead —
 * an empty dashed mark, faint.
 */

/** the colour of a topic's state */
const STATE: Record<Exclude<TreeTopic['status'], 'now' | 'next'>, Grad> = {
  done: 'mint',
  weak: 'coral',
};

/** The mark on the trunk. */
function Knot({ status }: { status: TreeTopic['status'] }) {
  if (status === 'next')
    return (
      <span
        aria-hidden
        className="relative z-10 size-10 shrink-0 rounded-full border-[1.5px] border-dashed bg-white"
        style={{ borderColor: '#D6D2C9' }}
      />
    );
  if (status === 'now')
    return (
      <span
        aria-hidden
        className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: DARK }}
      >
        <span className="relative flex size-2.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-white opacity-70" />
          <span className="relative size-full rounded-full bg-white" />
        </span>
      </span>
    );
  const tone = STATE[status];
  const Icon = status === 'done' ? CheckIcon : ArrowCounterClockwiseIcon;
  return (
    <span
      aria-hidden
      className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <Icon size={16} weight="bold" />
    </span>
  );
}

function Node({ topic, grade }: { topic: TreeTopic; grade: number | undefined }) {
  const { t } = useTranslation();
  const sim =
    topic.status === 'weak' || topic.status === 'now'
      ? suggestSims({
          subject: topic.subject,
          topic: topic.topic,
          ...(grade !== undefined && { grade }),
        })[0]
      : undefined;
  const facts = [
    topic.lessonsHeld > 0 &&
      t('tree.lessons', { held: topic.lessonsHeld, total: topic.lessonsTotal }),
    topic.avgPoints !== null &&
      t('tree.avg', { avg: topic.avgPoints.toString().replace('.', ',') }),
    topic.quizPct !== null && t('tree.quiz', { pct: topic.quizPct }),
  ].filter(Boolean);
  const status = t(`tree.status.${topic.status}`);

  if (topic.status === 'now') {
    // what is learnt now: a soft card in the subject's colour, its picture big and quiet behind
    const look = subjectLook(topic.subject);
    const tone = SUBJECT_GRAD[look.tone];
    return (
      <li className="relative flex gap-4 pb-4">
        <Knot status="now" />
        <div
          className="relative isolate min-w-0 flex-1 overflow-hidden rounded-[22px] p-4"
          style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
        >
          <SubjectArt
            icon={look.icon}
            tone={tone}
            size={128}
            ghost
            className="pointer-events-none absolute -top-5 -right-4 -z-10"
          />
          <span
            className="inline-flex h-6 items-center rounded-[7px] bg-white/80 px-2 text-[12px] leading-none font-medium"
            style={{ color: INK }}
          >
            {status}
          </span>
          <p
            className="mt-2 max-w-[85%] font-display text-[23px] leading-[29px] font-light tracking-[-0.02em]"
            style={{ color: INK }}
          >
            {topic.topic}
          </p>
          {facts.length > 0 && (
            <p className="mt-1 text-[13px]" style={{ color: INK_SOFT }}>
              {facts.join(' · ')}
            </p>
          )}
          {sim && (
            <Link
              to="/lab/sim/$slug"
              params={{ slug: sim.slug }}
              className={`${pillClass('light', 'sm')} mt-3`}
            >
              {t('tree.lab', { title: sim.title })}
              <PillIcon icon={ArrowUpRightIcon} size="sm" />
            </Link>
          )}
        </div>
      </li>
    );
  }

  const ahead = topic.status === 'next';
  const tone: Grad | undefined = topic.status === 'next' ? undefined : STATE[topic.status];
  return (
    <li className="relative flex gap-4 pb-4">
      <Knot status={topic.status} />
      <div className="min-w-0 flex-1 pt-1">
        <p
          className="text-[16px] leading-[22px] font-medium"
          style={{ color: ahead ? INK_FAINT : INK }}
        >
          {topic.topic}
        </p>
        <p
          className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]"
          style={{ color: ahead ? INK_FAINT : INK_SOFT }}
        >
          {tone ? (
            <span
              className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium"
              style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
            >
              {status}
            </span>
          ) : (
            <span>{status}</span>
          )}
          {facts.length > 0 && <span>{facts.join(' · ')}</span>}
        </p>
        {topic.status === 'weak' && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <span className="text-[13px]" style={{ color: INK_SOFT }}>
              {t('tree.repeat')}
            </span>
            {sim && (
              <Link
                to="/lab/sim/$slug"
                params={{ slug: sim.slug }}
                className={pillClass('light', 'sm')}
              >
                {t('tree.lab', { title: sim.title })}
                <PillIcon icon={ArrowUpRightIcon} size="sm" />
              </Link>
            )}
            <Link to="/materials" className={pillClass('light', 'sm')}>
              {t('tree.materials')}
              <PillIcon icon={ArrowUpRightIcon} size="sm" />
            </Link>
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * «Дерево знань»: the topics of a subject grow from the bottom up — done, to repeat, now, ahead;
 * the student and the family see it.
 */
export function TreePage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const tree = useKnowledgeTree(child?.memberId ?? '');
  const [picked, setPicked] = useState<string>();
  const subjects = tree.data ?? [];
  const current = subjects.find((s) => s.courseId === picked) ?? subjects[0];
  const done = subjects.reduce((n, s) => n + s.done, 0);
  const weak = subjects.reduce((n, s) => n + s.weak, 0);
  const empty = (
    <Block>
      <div className="flex flex-col items-center py-14 text-center">
        <TaskArt kind="catchUp" tone="mint" size={96} />
        <p className="mt-3 max-w-md text-[15px]" style={{ color: INK_SOFT }}>
          {t('tree.empty')}
        </p>
      </div>
    </Block>
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('tree.title')}
        note={subjects.length > 0 ? t('tree.summary', { done, weak }) : undefined}
        sub={t('tree.intro')}
      />

      {!child ? (
        empty
      ) : tree.isPending ? (
        <Spinner />
      ) : !current ? (
        empty
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          {/* the subjects: a list on the left; on a phone a row of chips to scroll */}
          <nav
            aria-label={t('tree.title')}
            className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4 lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:rounded-[28px] lg:bg-white lg:p-3"
          >
            {subjects.map((s) => {
              const on = s.courseId === current.courseId;
              const look = subjectLook(s.subject);
              const tone = SUBJECT_GRAD[look.tone];
              const share = s.topics.length ? s.done / s.topics.length : 0;
              return (
                <button
                  key={s.courseId}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setPicked(s.courseId)}
                  className={`flex shrink-0 items-center gap-3 rounded-full py-1.5 pr-4 pl-1.5 text-left transition-colors lg:rounded-[16px] lg:py-2 lg:pr-3 ${on ? '' : 'bg-white hover:bg-[#F5F3EF] lg:bg-transparent'}`}
                  style={on ? { backgroundColor: '#E9E5DC' } : undefined}
                >
                  <SubjectArt icon={look.icon} tone={tone} size={32} />
                  <span className="min-w-0 lg:flex-1">
                    <span
                      className={`block truncate text-[14.5px] leading-5 whitespace-nowrap ${on ? 'font-medium' : ''}`}
                      style={{ color: INK }}
                    >
                      {s.subject}
                    </span>
                    {/* how far the subject has grown */}
                    <span
                      aria-hidden
                      className="mt-1.5 hidden h-[3px] overflow-hidden rounded-full lg:block"
                      style={{ backgroundColor: on ? 'rgb(255 255 255 / 0.7)' : PAPER }}
                    >
                      <span
                        className="block h-full rounded-full"
                        style={{
                          width: `${Math.max(share * 100, s.done ? 6 : 0)}%`,
                          backgroundColor: GRAD[tone].ink,
                        }}
                      />
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 text-[12.5px]">
                    {s.weak > 0 && (
                      <span
                        className="inline-flex h-5 items-center rounded-[6px] px-1.5 text-[11.5px] leading-none font-medium"
                        style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
                        title={t('tree.status.weak')}
                      >
                        {s.weak}
                      </span>
                    )}
                    <span style={{ color: INK_FAINT }}>
                      {s.done}/{s.topics.length}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          <Block>
            <header className="mb-6 flex items-center gap-3">
              <SubjectArt
                icon={subjectLook(current.subject).icon}
                tone={SUBJECT_GRAD[subjectLook(current.subject).tone]}
                size={44}
              />
              <div className="min-w-0">
                <h2
                  className="font-display text-[30px] leading-[36px] font-light tracking-[-0.035em]"
                  style={{ color: INK }}
                >
                  {current.subject}
                </h2>
                <p className="text-[13px]" style={{ color: INK_FAINT }}>
                  {t('tree.summary', { done: current.done, weak: current.weak })}
                </p>
              </div>
            </header>
            <ol className="relative">
              {/* the trunk: the tree grows from the bottom up */}
              <span
                aria-hidden
                className="absolute top-3 bottom-8 left-[19px] w-0.5 rounded-full"
                style={{ backgroundColor: LINE }}
              />
              {[...current.topics].reverse().map((topic) => (
                <Node key={topic.topic} topic={topic} grade={child.class?.grade} />
              ))}
            </ol>
          </Block>
        </div>
      )}
    </div>
  );
}
